import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { PauseFilled, PlayFilledAlt, Repeat } from '@carbon/icons-react';
import { useInterval } from '../../../hooks/useInterval';
import useScreenSize from '../../../hooks/useScreenSize';

import './playbar.scss';

const getBackgroundSize = (value, startVal, endVal) => {
	const size = ((value - startVal) * 100) / (endVal - startVal);
	return size < 0 ? `0% 100%` : `${size}% 100%`;
};

const generateClipPath = (segmentWidth = 100, numberOfSegments) => {
	const segments = Array(numberOfSegments).fill(undefined);
	const padding = 2;
	const gap = 5;
	const width = segmentWidth - padding - gap;

	const segmentPaths = segments
		.map((x, i) => {
			const offset =
				i === 0 ? (i + 1) * (width + padding) : (i + 1) * (width + padding + gap) - gap;
			return `M ${offset} 0 a 2 2 0 0 1 2 2 v 11 a 2 2 0 0 1 -2 2 h -${width} a 2 2 0 0 1 -2 -2 v -11 a 2 2 0 0 1 2 -2`;
		})
		.join(' ');

	return numberOfSegments === 0 ? null : segmentPaths;
};

const TimeDisplay = ({ timestamp }) => {
	const timeObject = dayjs.unix(timestamp);
	const date = timeObject.format('ddd D MMM');
	const time = timeObject.format('HH:mm');
	return (
		<>
			<span className="playbar-date">{date}</span>
			<span className="playbar-time">{time}</span>
		</>
	);
};

const EndTimeDisplay = ({ timestamp }) => {
	const timeObject = dayjs.unix(timestamp);
	const time = timeObject.format('HH:mm');
	return <span className="playbar-end-time">{time}</span>;
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v)); // Clamps a value between a minimum and maximum

type MarkerItem = {
	startTime: number;
	endTime: number;
	label: string;
};

type GroupedMarker = {
	time: number;
	items: MarkerItem[];
};

type Marker = GroupedMarker;

const formatRange = (start: number, end: number, endWindow: number) => {
	const startStr = dayjs.unix(start).format('HH:mm');
	const endStr =
		!Number.isFinite(end) || end >= endWindow ? 'ongoing' : dayjs.unix(end).format('HH:mm');
	return `${startStr} – ${endStr}`;
};

interface PlayBarProps {
	startTime?: number;
	endTime?: number;

	multiplierValues?: number[];
	numberOfSegments?: number;
	step?: number;
	stepsPerSecond?: number;
	position?: string;

	defaultOnLoad?: boolean;
	playOnLoad?: boolean;
	loopingOnLoad?: boolean;

	onPlayBarValueStateChange?: (playBarValue: number) => void;
	onDefaultStateChange?: (isDefault: boolean) => void;
	onPlayStateChange?: (isPlaying: boolean) => void;
	onLoopStateChange?: (isLooping: boolean) => void;
	outline?: boolean;

	externalSetValue?: (utils: {
		setPlayBarValue: (val: number) => void;
		setEndTimeValue: (val: number) => void;
		setStartTimeValue: (val: number) => void;
		getStartTimeValue: () => number;
		getEndTimeValue: () => number;
		getStepSeconds: () => number;
		stopAll: () => void;
		getPlayheadRect: () => DOMRect | null;
	}) => void;

	markers?: Marker[];
}

const PlayBar = ({
	startTime = dayjs().unix(),
	endTime = dayjs().add(12, 'hour').unix(),

	multiplierValues = [1, 2],
	numberOfSegments = 0,
	step = 3600,
	stepsPerSecond = 1,
	position = 'none',

	outline = true,
	defaultOnLoad = true,
	playOnLoad = false,
	loopingOnLoad = false,

	onPlayBarValueStateChange = () => {},
	onDefaultStateChange = () => {},
	onPlayStateChange = () => {},
	onLoopStateChange = () => {},

	externalSetValue,

	markers,
}: PlayBarProps) => {
	const [startTimeState, setStartTimeState] = useState(startTime);
	const [endTimeState, setEndTimeState] = useState(endTime);
	const [playBarValue, setPlayBarValue] = useState(startTimeState ?? dayjs().unix());
	const [inputWidth, setInputWidth] = useState(0);
	const [multiplierIndex, setMultiplierIndex] = useState(0);

	const [isDefault, setIsDefault] = useState(defaultOnLoad);
	const [isPlaying, setIsPlaying] = useState(playOnLoad);
	const [isLooping, setIsLooping] = useState(loopingOnLoad);

	const [delay, setDelay] = useState(
		isPlaying ? 1000 / stepsPerSecond / multiplierValues[multiplierIndex] : null,
	);
	const inputRef = useRef<HTMLInputElement>(null);
	const screenSize = useScreenSize();
	const playheadAnchorRef = useRef<HTMLDivElement>(null);

	const pct = ((playBarValue - startTimeState) * 100) / (endTimeState - startTimeState || 1);

	const playheadLeft = `${clamp(pct, 0, 100)}%`;

	useEffect(
		() => onPlayBarValueStateChange(playBarValue),
		[playBarValue, onPlayBarValueStateChange],
	);
	useEffect(() => onDefaultStateChange?.(isDefault), [isDefault, onDefaultStateChange]);
	useEffect(() => onPlayStateChange?.(isPlaying), [isPlaying, onPlayStateChange]);
	useEffect(() => onLoopStateChange?.(isLooping), [isLooping, onLoopStateChange]);

	useEffect(() => {
		setDelay(isPlaying ? 1000 / (stepsPerSecond * multiplierValues[multiplierIndex]) : null);
	}, [isPlaying, stepsPerSecond, multiplierIndex, multiplierValues]);

	useEffect(() => {
		externalSetValue?.({
			setPlayBarValue: (v: number) => setPlayBarValue(v),
			setEndTimeValue: (end: number) => {
				setEndTimeState(end);
				setPlayBarValue((prev) => clamp(prev ?? startTimeState, startTimeState, end));
			},
			setStartTimeValue: (ts: number) => {
				setStartTimeState(ts);
				setPlayBarValue((prev) => clamp(prev ?? ts, ts, endTimeState));
			},
			getStartTimeValue: () => startTimeState,
			getEndTimeValue: () => endTimeState,
			getStepSeconds: () => step,
			stopAll: () => {
				setIsDefault(true);
				setIsPlaying(false);
				setIsLooping(false);
			},
			getPlayheadRect: () => playheadAnchorRef.current?.getBoundingClientRect() ?? null,
		});
	}, [externalSetValue, startTimeState, endTimeState]);

	useLayoutEffect(() => {
		setPlayBarValue((prev) => clamp(prev ?? startTimeState, startTimeState, endTimeState));
	}, [startTimeState, endTimeState]);

	useEffect(() => {
		if (isDefault && !isPlaying) {
			setStartTimeState(startTime);
			setEndTimeState(endTime);
			setPlayBarValue((prev) => clamp(prev ?? startTime, startTime, endTime));
		}
	}, [isDefault, startTime, endTime]);

	useEffect(() => {
		setIsDefault(playBarValue == startTimeState);
	}, [playBarValue, startTimeState, isDefault, isPlaying]);

	useLayoutEffect(() => {
		if (inputRef.current) {
			setInputWidth(inputRef.current.offsetWidth);
		}
	}, [screenSize]);

	useInterval(() => {
		const next = playBarValue + step;
		if (next <= endTimeState) {
			setPlayBarValue(next);
			return;
		}
		if (isLooping) {
			setPlayBarValue(startTimeState);
			setIsPlaying(true);
		} else {
			setPlayBarValue(startTimeState);
			setIsPlaying(false);
		}
	}, delay);

	const ImpactMarkerIcon = ({ size = 24 }: { size?: number }) => (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
			focusable="false"
		>
			<path
				d="M20.9398 19.4693C20.8521 19.6253 20.7242 19.7549 20.5694 19.8446C20.4147 19.9343 20.2386 19.9808 20.0598 19.9793H3.93976C3.76087 19.9808 3.58485 19.9343 3.43007 19.8446C3.27529 19.7549 3.14741 19.6253 3.05976 19.4693C2.97199 19.3173 2.92578 19.1449 2.92578 18.9693C2.92578 18.7938 2.97199 18.6213 3.05976 18.4693L11.0598 4.46933C11.1437 4.30553 11.2712 4.16807 11.4282 4.07208C11.5852 3.97608 11.7657 3.92529 11.9498 3.92529C12.1338 3.92529 12.3143 3.97608 12.4713 4.07208C12.6283 4.16807 12.7558 4.30553 12.8398 4.46933L20.8898 18.4693C20.989 18.6192 21.046 18.793 21.0548 18.9725C21.0636 19.152 21.0238 19.3305 20.9398 19.4893V19.4693Z"
				fill="#DE1E32"
			/>
			<path
				d="M12 16C11.8022 16 11.6088 16.0587 11.4444 16.1686C11.2799 16.2784 11.1518 16.4346 11.0761 16.6173C11.0004 16.8001 10.9806 17.0011 11.0192 17.1951C11.0578 17.3891 11.153 17.5673 11.2929 17.7071C11.4327 17.847 11.6109 17.9422 11.8049 17.9808C11.9989 18.0194 12.1999 17.9996 12.3827 17.9239C12.5654 17.8482 12.7216 17.72 12.8314 17.5556C12.9413 17.3911 13 17.1978 13 17C13 16.7348 12.8946 16.4805 12.7071 16.2929C12.5195 16.1054 12.2652 16 12 16ZM22.67 17.47L14.62 3.47003C14.3598 3.00354 13.9798 2.61498 13.5192 2.3445C13.0586 2.07401 12.5341 1.9314 12 1.9314C11.4658 1.9314 10.9414 2.07401 10.4808 2.3445C10.0202 2.61498 9.64016 3.00354 9.37997 3.47003L1.37997 17.47C1.11076 17.924 0.96611 18.441 0.960612 18.9688C0.955114 19.4966 1.08897 20.0166 1.34865 20.4761C1.60834 20.9356 1.98467 21.3185 2.43965 21.5861C2.89463 21.8536 3.41215 21.9964 3.93997 22H20.06C20.592 22.0053 21.1159 21.8689 21.5779 21.6049C22.0399 21.341 22.4233 20.9589 22.6889 20.4978C22.9546 20.0368 23.0928 19.5134 23.0895 18.9814C23.0861 18.4493 22.9414 17.9277 22.67 17.47ZM20.94 19.47C20.8523 19.626 20.7244 19.7556 20.5697 19.8453C20.4149 19.935 20.2389 19.9815 20.06 19.98H3.93997C3.76108 19.9815 3.58507 19.935 3.43029 19.8453C3.2755 19.7556 3.14762 19.626 3.05997 19.47C2.9722 19.318 2.92599 19.1456 2.92599 18.97C2.92599 18.7945 2.9722 18.622 3.05997 18.47L11.06 4.47003C11.1439 4.30623 11.2714 4.16876 11.4284 4.07277C11.5854 3.97678 11.7659 3.92599 11.95 3.92599C12.134 3.92599 12.3145 3.97678 12.4715 4.07277C12.6286 4.16876 12.7561 4.30623 12.84 4.47003L20.89 18.47C20.9892 18.6199 21.0462 18.7937 21.055 18.9732C21.0638 19.1527 21.0241 19.3312 20.94 19.49V19.47ZM12 8.00003C11.7348 8.00003 11.4804 8.10538 11.2929 8.29292C11.1053 8.48046 11 8.73481 11 9.00003V13C11 13.2652 11.1053 13.5196 11.2929 13.7071C11.4804 13.8947 11.7348 14 12 14C12.2652 14 12.5195 13.8947 12.7071 13.7071C12.8946 13.5196 13 13.2652 13 13V9.00003C13 8.73481 12.8946 8.48046 12.7071 8.29292C12.5195 8.10538 12.2652 8.00003 12 8.00003Z"
				fill="white"
			/>
		</svg>
	);

	const ImpactTooltipIcon = ({ size = 42 }: { size?: number }) => (
		<svg
			width={size}
			height={size}
			viewBox="0 0 34 35"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<circle cx="17" cy="17.5" r="16" fill="#F46A6A" stroke="white" strokeWidth="2" />
			<path
				d="M17.0004 19.7222C16.8246 19.7222 16.6527 19.7744 16.5066 19.8721C16.3604 19.9697 16.2465 20.1086 16.1792 20.271C16.1119 20.4334 16.0943 20.6121 16.1286 20.7846C16.1629 20.957 16.2476 21.1154 16.3719 21.2397C16.4962 21.364 16.6546 21.4486 16.827 21.4829C16.9994 21.5172 17.1781 21.4996 17.3406 21.4324C17.503 21.3651 17.6418 21.2512 17.7395 21.105C17.8372 20.9588 17.8893 20.7869 17.8893 20.6111C17.8893 20.3754 17.7956 20.1493 17.6289 19.9826C17.4622 19.8159 17.2362 19.7222 17.0004 19.7222ZM26.4849 21.0289L19.3293 8.58447C19.098 8.16982 18.7602 7.82443 18.3508 7.584C17.9414 7.34356 17.4752 7.2168 17.0004 7.2168C16.5256 7.2168 16.0594 7.34356 15.65 7.584C15.2406 7.82443 14.9028 8.16982 14.6715 8.58447L7.56041 21.0289C7.32111 21.4325 7.19253 21.892 7.18764 22.3612C7.18276 22.8304 7.30174 23.2925 7.53257 23.701C7.7634 24.1094 8.09792 24.4498 8.50235 24.6876C8.90677 24.9254 9.36679 25.0524 9.83596 25.0556H24.1649C24.6378 25.0602 25.1035 24.939 25.5141 24.7044C25.9248 24.4698 26.2656 24.1301 26.5017 23.7203C26.7378 23.3105 26.8607 22.8453 26.8577 22.3723C26.8548 21.8994 26.7261 21.4357 26.4849 21.0289ZM24.9471 22.8067C24.8692 22.9453 24.7555 23.0605 24.6179 23.1402C24.4803 23.22 24.3239 23.2613 24.1649 23.26H9.83596C9.67695 23.2613 9.52049 23.22 9.38291 23.1402C9.24533 23.0605 9.13165 22.9453 9.05374 22.8067C8.97572 22.6716 8.93465 22.5183 8.93465 22.3622C8.93465 22.2062 8.97572 22.0529 9.05374 21.9178L16.1649 9.47336C16.2394 9.32776 16.3528 9.20556 16.4924 9.12024C16.6319 9.03492 16.7924 8.98977 16.956 8.98977C17.1196 8.98977 17.28 9.03492 17.4196 9.12024C17.5591 9.20556 17.6725 9.32776 17.7471 9.47336L24.9026 21.9178C24.9908 22.051 25.0415 22.2055 25.0493 22.365C25.0571 22.5246 25.0218 22.6833 24.9471 22.8245V22.8067ZM17.0004 12.6111C16.7647 12.6111 16.5386 12.7048 16.3719 12.8715C16.2052 13.0382 16.1115 13.2643 16.1115 13.5V17.0556C16.1115 17.2913 16.2052 17.5174 16.3719 17.6841C16.5386 17.8508 16.7647 17.9445 17.0004 17.9445C17.2362 17.9445 17.4622 17.8508 17.6289 17.6841C17.7956 17.5174 17.8893 17.2913 17.8893 17.0556V13.5C17.8893 13.2643 17.7956 13.0382 17.6289 12.8715C17.4622 12.7048 17.2362 12.6111 17.0004 12.6111Z"
				fill="#2A2A26"
			/>
		</svg>
	);

	return (
		<div className="playbar-container">
			<button
				className="playbar-play-pause playbar-button"
				onClick={() => {
					if (isPlaying) {
						setIsPlaying(false);
					} else {
						if (playBarValue >= endTimeState || playBarValue < startTimeState) {
							setPlayBarValue(startTimeState);
						}
						setIsPlaying(true);
					}
				}}
				aria-label={isPlaying ? `Pause` : 'Play'}
			>
				{isPlaying ? <PauseFilled size={32} /> : <PlayFilledAlt size={32} />}
			</button>
			<TimeDisplay timestamp={playBarValue} />
			<div style={{ width: '100%', position: 'relative' }}>
				{/* Impact markers overlay */}
				{Array.isArray(markers) &&
					markers
						.filter((m) => {
							const t = m.time;
							return Number.isFinite(t) && t >= startTimeState && t <= endTimeState;
						})
						.map((m, i) => {
							const markerTime = m.time;
							const pct =
								((markerTime - startTimeState) * 100) / (endTimeState - startTimeState || 1);
							const left = `${clamp(pct, 0, 100)}%`;
							const timeText = dayjs.unix(markerTime).format('HH:mm');
							const assetName = m.items[0].label?.replace(/\s*impacted.*/i, '') || 'Asset';
							const tooltipId = `pb-tip-${markerTime}-${i}`;
							const aria = `Jump to impacted time ${timeText} for ${assetName}`;
							return (
								<div
									key={`pb-mark-${markerTime}-${i}`}
									className="playbar-marker"
									style={{ left }}
									role="img"
									aria-label={aria}
									aria-describedby={tooltipId}
									tabIndex={0}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											setPlayBarValue(markerTime);
											onPlayBarValueStateChange?.(markerTime);
											setIsPlaying(false);
											setIsLooping(false);
										}
									}}
								>
									<ImpactMarkerIcon size={24} />
									<div className="playbar-tooltip" role="tooltip" id={tooltipId}>
										<ImpactTooltipIcon size={42} />
										<div className="playbar-tooltip-text">
											{m.items.map((item, idx) => (
												<div key={idx} className="mt-0.5 mb-1">
													<div className="playbar-tooltip-time">
														{formatRange(item.startTime, item.endTime, endTimeState)}
													</div>
													<div className="playbar-tooltip-label">{item.label}</div>
												</div>
											))}
										</div>
									</div>
								</div>
							);
						})}

				<div
					ref={playheadAnchorRef}
					className="playbar-playhead-anchor"
					style={{ left: playheadLeft }}
					aria-hidden="true"
				/>

				<input
					ref={inputRef}
					className="playbar-timeline"
					type="range"
					min={startTimeState}
					max={endTimeState}
					step={step}
					value={playBarValue ?? startTimeState}
					onChange={(e) => {
						const value = e.currentTarget.valueAsNumber;
						if (Number.isFinite(value)) {
							setPlayBarValue(value);
						}
					}}
					onInput={(e) => {
						const v = (e.currentTarget as HTMLInputElement).valueAsNumber;
						onPlayBarValueStateChange(v);
						setIsPlaying(false);
						setIsLooping(false);
					}}
					style={{
						['--playbar-background-size' as any]: getBackgroundSize(
							playBarValue ?? startTimeState,
							startTimeState,
							endTimeState,
						),
						clipPath:
							numberOfSegments && inputWidth
								? `path("${generateClipPath(inputWidth / numberOfSegments, numberOfSegments)}")`
								: undefined,
					}}
					aria-label="Rain radar playback timeline"
					aria-valuemin={startTimeState}
					aria-valuemax={endTimeState}
					aria-valuenow={playBarValue ?? startTimeState}
					aria-valuetext={dayjs.unix(playBarValue ?? startTimeState).format('ddd D MMM HH:mm')}
				/>
			</div>
			<EndTimeDisplay timestamp={endTimeState} />
			<button
				className="playbar-autoplay playbar-button"
				onClick={() => {
					setIsLooping((prev) => {
						const next = !prev;
						// if we turn loop on while sitting at the end, reset so the display updates immediately
						if (next && !isPlaying && playBarValue >= endTimeState) {
							setPlayBarValue(startTimeState);
						}
						return next;
					});
				}}
				aria-label={`Autoplay is ${isLooping ? 'on ' : 'off'}, press to turn ${isLooping ? 'off ' : 'on'}`}
			>
				<Repeat size={22} />
				{isLooping ? 'On' : 'Off'}
			</button>
			<button
				className="playbar-time-multiplier playbar-button"
				onClick={() => setMultiplierIndex((multiplierIndex + 1) % multiplierValues.length)}
				aria-label={`Current speed is ${multiplierValues[multiplierIndex]}x. Press to increase to ${multiplierValues[(multiplierIndex + 1) % multiplierValues.length]}x`}
			>
				<span className="playbar-multiplier-value">{`${multiplierValues[multiplierIndex]}x`}</span>
				<span className="playbar-multiplier-descriptor">Speed</span>
			</button>
		</div>
	);
};

export default PlayBar;
